import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Lock, Flask, CheckCircle, WarningCircle } from '@phosphor-icons/react';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await api.request('POST', '/auth/reset-password', { token, password });
      setSuccess(true);
      toast.success('Password updated!');
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Failed to reset password. The link may be expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="auth-container">
        <div className="auth-card text-center">
          <WarningCircle size={40} className="mx-auto mb-3 text-red-400" />
          <h2 className="text-lg font-semibold mb-2" style={{ color: '#F1F5F9' }}>Invalid Reset Link</h2>
          <p className="text-sm mb-4" style={{ color: '#94A3B8' }}>This link is missing or invalid. Please request a new password reset.</p>
          <Button className="btn-primary" onClick={() => navigate('/login')}>Back to Login</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ background: 'linear-gradient(135deg, #6366F1, #06B6D4)' }}>
            <Flask size={28} className="text-white" />
          </div>
          <h1 className="text-xl font-bold" style={{ color: '#F8FAFC' }}>Set New Password</h1>
          <p className="text-sm mt-1" style={{ color: '#94A3B8' }}>Chaturvedi Lab</p>
        </div>

        {success ? (
          <div className="text-center" data-testid="reset-success-msg">
            <CheckCircle size={48} className="mx-auto mb-3" style={{ color: '#818CF8' }} />
            <h2 className="font-semibold mb-2" style={{ color: '#F1F5F9' }}>Password Updated!</h2>
            <p className="text-sm mb-4" style={{ color: '#94A3B8' }}>You can now sign in with your new password.</p>
            <Button className="btn-primary" onClick={() => navigate('/login')} data-testid="back-to-login-btn">
              Sign In
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg mb-4" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }} data-testid="reset-error">
                <WarningCircle size={16} style={{ color: '#F87171' }} />
                <span className="text-sm" style={{ color: '#FCA5A5' }}>{error}</span>
              </div>
            )}

            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium uppercase mb-1 block" style={{ color: '#94A3B8' }}>New Password</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#64748B' }} />
                  <Input
                    type="password"
                    placeholder="At least 6 characters"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="input-field pl-10"
                    required
                    data-testid="new-password-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium uppercase mb-1 block" style={{ color: '#94A3B8' }}>Confirm Password</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#64748B' }} />
                  <Input
                    type="password"
                    placeholder="Repeat your password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    className="input-field pl-10"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
            </div>

            <Button type="submit" className="btn-primary w-full mt-4" disabled={loading} data-testid="reset-submit-btn">
              {loading ? 'Updating...' : 'Update Password'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
